import { ServiceConfigoption } from '@/modules/Service/types'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import EditIcons from '@/icons/EditIcons'
import { formatNumeral } from '@/utils/function'

interface ConfigPriceRangesViewOnlyProps {
    data: ServiceConfigoption[]
    serviceID: number
    show_price_list?: boolean
    onEditMode?: () => void
}

const ConfigPriceRangesViewOnly: React.FC<ConfigPriceRangesViewOnlyProps> = ({
    data,
    onEditMode,
}) => {
    const { t } = useTranslation()
    const [editMode, setEditMode] = useState(false)

    const isUnlimited = (value: string | number): boolean => {
        return Number(value) >= 999999999
    }

    // If edit mode is enabled, notify parent to switch to edit mode
    if (editMode) {
        // We'll handle this through a callback prop instead of circular import
        // For now, just show a message - the parent component will handle the switch
        return (
            <div className="mt-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-center py-8">
                        <div className="text-gray-500">
                            {t(
                                'switching-to-edit-mode',
                                'Đang chuyển sang chế độ chỉnh sửa...'
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="mt-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                {/* Header */}
                <div className="border-b border-gray-200 pb-4 mb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typogaphy className="text-lg font-semibold text-gray-800">
                                {t('services.price-exceed.title')}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-gray-600">
                                {t('services.price-exceed.subtitle')}
                            </Typogaphy>
                        </div>
                        <div className="flex items-center gap-2">
                            <Typogaphy className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                {data?.length || 0}{' '}
                                {t('services.configurations')}
                            </Typogaphy>
                            <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<EditIcons />}
                                onClick={() => {
                                    if (onEditMode) {
                                        onEditMode()
                                    } else {
                                        setEditMode(true)
                                    }
                                }}
                                className="border-dashed"
                            >
                                {t('edit', 'Chỉnh sửa')}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Config Options */}
                <div className="space-y-6">
                    {data
                        ?.filter((configoption) => configoption.statementable)
                        ?.map((configoption, index) => (
                            <div
                                key={configoption.service_configoption_id}
                                className="border border-gray-200 rounded-lg p-4"
                            >
                                {/* Config Header */}
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <span className="text-blue-600 font-semibold text-sm">
                                                {index + 1}
                                            </span>
                                        </div>
                                        <div>
                                            <Typogaphy className="font-medium text-gray-800">
                                                {t(configoption.name)}
                                            </Typogaphy>
                                            <Typogaphy className="text-xs text-gray-500">
                                                {t('services.config-option')}
                                            </Typogaphy>
                                        </div>
                                    </div>
                                    <div className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                        {t('services.price-exceed.active')}
                                    </div>
                                </div>

                                {/* Calculation Method */}
                                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                    <Typogaphy className="text-xs text-gray-500 mb-1">
                                        {t('form-of-comparison')}
                                    </Typogaphy>
                                    <Typogaphy className="font-medium text-gray-800">
                                        {configoption.method
                                            ?.calc_amount_method ===
                                        'progressive'
                                            ? t(
                                                  'services.calc-method.progressive'
                                              )
                                            : t(
                                                  'services.calc-method.agreement'
                                              )}
                                    </Typogaphy>
                                </div>

                                {configoption.price_ranges &&
                                    configoption.price_ranges.length > 0 && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <svg
                                                        className="w-3 h-3 text-blue-600"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1}
                                                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                                        />
                                                    </svg>
                                                </div>
                                                <Typogaphy className="font-medium text-blue-900 text-sm">
                                                    {t(
                                                        'services.pricing-tiers'
                                                    )}
                                                </Typogaphy>
                                            </div>

                                            <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                                                {/* Header */}
                                                <div className="grid grid-cols-4 gap-4 p-3 bg-gray-50 border-b border-gray-200">
                                                    <Typogaphy className="text-xs font-medium text-gray-600">
                                                        {t('services.tier')}
                                                    </Typogaphy>
                                                    <Typogaphy className="text-xs font-medium text-gray-600">
                                                        {t(
                                                            'services.usage-from'
                                                        )}
                                                    </Typogaphy>
                                                    <Typogaphy className="text-xs font-medium text-gray-600">
                                                        {t('services.usage-to')}
                                                    </Typogaphy>
                                                    <Typogaphy className="text-xs font-medium text-gray-600">
                                                        {t(
                                                            'services.price-per-unit'
                                                        )}
                                                    </Typogaphy>
                                                </div>

                                                {/* Price Ranges */}
                                                {configoption.price_ranges
                                                    ?.sort(
                                                        (a, b) =>
                                                            Number(
                                                                a.price_start
                                                            ) -
                                                            Number(
                                                                b.price_start
                                                            )
                                                    )
                                                    .map(
                                                        (range, rangeIndex) => (
                                                            <div
                                                                key={rangeIndex}
                                                                className="grid grid-cols-4 gap-4 p-3 border-b border-gray-100 last:border-b-0"
                                                            >
                                                                <div />
                                                                <Typogaphy className="text-sm text-gray-600">
                                                                    {formatNumeral(
                                                                        range.price_start,
                                                                        0
                                                                    )}
                                                                </Typogaphy>
                                                                <Typogaphy className="text-sm text-gray-600">
                                                                    {isUnlimited(
                                                                        range.price_end
                                                                    ) ? (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                                                            <span>
                                                                                ∞
                                                                            </span>
                                                                            <span>
                                                                                Unlimited
                                                                            </span>
                                                                        </span>
                                                                    ) : (
                                                                        formatNumeral(
                                                                            range.price_end,
                                                                            0
                                                                        )
                                                                    )}
                                                                </Typogaphy>
                                                                <Typogaphy className="text-sm font-medium text-gray-800">
                                                                    {formatNumeral(
                                                                        range.price_per_unit,
                                                                        0
                                                                    )}
                                                                    đ
                                                                </Typogaphy>
                                                            </div>
                                                        )
                                                    )}
                                            </div>
                                        </div>
                                    )}
                            </div>
                        ))}
                </div>
            </div>
        </div>
    )
}

export default ConfigPriceRangesViewOnly
